import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import CartContext from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import CartDrawer from '../components/CartDrawer';
import { resolveImageUrl } from '../utils/api';
import { lineUnitPrice, lineVariantLabel, groupCartLines } from '../utils/cartLine';

const CartPage = () => {
    const { cart, updateCartItem, removeFromCart } = useContext(CartContext);
    const { convertPrice, getCurrencySymbol } = useCurrency();
    const navigate = useNavigate();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const total = cart?.items?.reduce((sum, item) => {
        const price = lineUnitPrice(item);
        return sum + price * item.quantity;
    }, 0) || 0;

    return (
        <>
            <div className="overflow-x-hidden">
                {/* ── HEADER ─────────────────────────────────── */}
                <section
                    className="relative pt-36 pb-16 overflow-hidden"
                    style={{ background: 'linear-gradient(135deg, #0a0a14 0%, #0f172a 60%, #1e1b4b 100%)' }}
                >
                    <div className="absolute top-1/2 right-1/3 w-72 h-72 rounded-full opacity-15 pointer-events-none"
                        style={{ background: 'radial-gradient(circle, #3b82f6 0%, transparent 70%)', transform: 'translateY(-50%)' }} />
                    <div className="relative max-w-6xl mx-auto px-6">
                        <h1 className="text-5xl sm:text-6xl font-bold text-white mb-2 tracking-tight">Shopping Cart</h1>
                        <p className="text-slate-400 font-light">{cart?.items?.length || 0} item{(cart?.items?.length || 0) !== 1 ? 's' : ''} in your cart</p>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0">
                        <svg viewBox="0 0 1440 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                            <path d="M0 40L720 10L1440 40V0H0Z" fill="#f8fafc" />
                        </svg>
                    </div>
                </section>

                <div className="min-h-screen bg-slate-50 pb-24">
                    <div className="max-w-6xl mx-auto px-6 pt-12">
                        {!cart || cart.items.length === 0 ? (
                            <div className="text-center py-24">
                                <div className="w-24 h-24 rounded-3xl bg-white border border-slate-100 flex items-center justify-center mx-auto mb-8 shadow-sm">
                                    <svg className="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 mb-3">Your cart is empty</h3>
                                <p className="text-slate-500 font-light mb-8">Looks like you haven't added anything yet.</p>
                                <button onClick={() => navigate('/catalog')}
                                    className="inline-flex items-center gap-2 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                                    style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)', boxShadow: '0 8px 24px rgba(99,102,241,0.3)' }}>
                                    Continue Shopping →
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                {/* One card per product; each variant is a row inside it. */}
                                <div className="lg:col-span-2 space-y-4">
                                    {groupCartLines(cart.items).map(group => (
                                        <div key={group.key}
                                            className="bg-white rounded-3xl p-5 border border-slate-100 flex gap-5 items-start">
                                            <img src={resolveImageUrl(group.product?.image)} alt={group.product?.name}
                                                className="w-24 h-24 object-cover rounded-2xl flex-shrink-0" />

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-baseline justify-between gap-3 mb-3">
                                                    <h3 className="text-base font-semibold text-slate-900 truncate">{group.product?.name}</h3>
                                                    {group.lines.length > 1 && (
                                                        <span className="text-sm font-semibold text-slate-900 whitespace-nowrap">
                                                            {getCurrencySymbol()}{convertPrice(group.subtotal)}
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="divide-y divide-slate-100">
                                                    {group.lines.map(item => (
                                                        <div key={item.variantId || 'default'} className="flex items-center justify-between gap-3 py-2.5 first:pt-0">
                                                            <div className="min-w-0">
                                                                {lineVariantLabel(item) && (
                                                                    <p className="text-sm font-medium text-slate-700 truncate">{lineVariantLabel(item)}</p>
                                                                )}
                                                                <p className="text-blue-600 font-semibold text-sm">
                                                                    {getCurrencySymbol()}{convertPrice(lineUnitPrice(item))}
                                                                    <span className="text-slate-400 font-normal"> each</span>
                                                                </p>
                                                            </div>

                                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                                <button
                                                                    onClick={() => updateCartItem(group.product?._id, item.quantity - 1, item.variantId)}
                                                                    className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium transition-all flex items-center justify-center"
                                                                    aria-label="Decrease quantity"
                                                                >−</button>
                                                                <span className="w-7 text-center font-semibold text-slate-900 text-sm">{item.quantity}</span>
                                                                <button
                                                                    onClick={() => updateCartItem(group.product?._id, item.quantity + 1, item.variantId)}
                                                                    className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium transition-all flex items-center justify-center"
                                                                    aria-label="Increase quantity"
                                                                >+</button>
                                                                <button
                                                                    onClick={() => removeFromCart(group.product?._id, item.variantId)}
                                                                    className="text-slate-400 hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-red-50"
                                                                    aria-label={`Remove ${lineVariantLabel(item) || group.product?.name}`}
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Summary */}
                                <div className="h-fit">
                                    <div className="bg-white rounded-3xl p-7 border border-slate-100 sticky top-24">
                                        <h2 className="text-xl font-bold text-slate-900 mb-6">Order Summary</h2>
                                        <div className="space-y-3 border-b border-slate-100 pb-5 mb-5">
                                            <div className="flex justify-between text-slate-600 font-light text-sm">
                                                <span>Subtotal</span>
                                                <span className="font-medium text-slate-900">{getCurrencySymbol()}{convertPrice(total)}</span>
                                            </div>
                                            <div className="flex justify-between text-slate-600 font-light text-sm">
                                                <span>Shipping</span>
                                                <span className="text-emerald-600 font-medium">Free</span>
                                            </div>
                                        </div>
                                        <div className="flex justify-between font-bold text-slate-900 text-lg mb-6">
                                            <span>Total</span>
                                            <span>{getCurrencySymbol()}{convertPrice(total)}</span>
                                        </div>
                                        <button onClick={() => navigate('/checkout')}
                                            className="w-full text-white font-semibold py-4 rounded-2xl transition-all duration-300 hover:-translate-y-0.5"
                                            style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)', boxShadow: '0 8px 24px rgba(99,102,241,0.25)' }}>
                                            Proceed to Checkout
                                        </button>
                                        <button onClick={() => navigate('/catalog')}
                                            className="w-full mt-3 text-slate-500 hover:text-slate-700 font-medium py-3 rounded-2xl transition-all duration-300 bg-slate-50 hover:bg-slate-100 text-sm">
                                            Continue Shopping
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <CartDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
        </>
    );
};

export default CartPage;
